import { RefObject, useCallback, useEffect, useState } from 'react';

import { useWindowIsResizing } from '.';

/**
 * @typedef {UseAutoSizeInput}
 * @property {number} bottomOffset optional gap between element and bottom of viewport
 * @property {RefObject<HTMLElement>} elementRef a ref for the element to be sized
 * @property {number} minHeight optional minimum height
 * @property {number} minWidth optional minimum width
 * @property {number} resizeRate rate in milliseconds at which to calculate the size while the
 * window is being resized
 * @property {number} rightOffset optional gap between element and right side of viewport
 */
interface UseAutoSizeInput {
    bottomOffset?: number;
    elementRef: RefObject<HTMLElement>;
    minHeight?: number;
    minWidth?: number;
    resizeRate: number;
    rightOffset?: number;
}

/**
 * @typedef {UseAutoSizeResult}
 * @property {function} getSize a callback that can be invoked to calculate the size. This is useful
 * when the layout of the page changes after the calling component has mounted. This function does
 * not return anything; rather, it updates the value of `height` and `width` if necessary.
 * @property {number} height calculated height
 * @property {number} width calculated width
 */
interface UseAutoSizeResult {
    getSize(): void;
    height: number;
    width: number;
}

/**
 * Automatically sets a width and height for an element from a ref, assuming that the element should
 * grow downward and to the right to fill the viewport.
 * @param {UseAutoSizeInput} input
 * @returns {UseAutoSizeResult}
 */
export const useAutoSize = ({
    bottomOffset = 0,
    elementRef,
    minHeight = 0,
    minWidth = 0,
    resizeRate,
    rightOffset = 0,
}: UseAutoSizeInput): UseAutoSizeResult => {
    const [height, setHeight] = useState(0);
    const [width, setWidth] = useState(0);
    const { windowIsResizing } = useWindowIsResizing(resizeRate);

    const getSize = useCallback((): void => {
        if (elementRef.current !== null) {
            const { top, left } = elementRef.current.getBoundingClientRect();

            const viewportHeight = Math.max(
                document.documentElement.clientHeight,
                window.innerHeight || 0,
            );

            const viewportWidth = Math.max(
                document.documentElement.clientWidth,
                window.innerWidth || 0,
            );

            const calculatedHeight = viewportHeight - top - bottomOffset;
            const newHeight = Math.max(calculatedHeight, minHeight);
            const calculatedWidth = viewportWidth - left - rightOffset;
            const newWidth = Math.max(calculatedWidth, minWidth);

            setHeight(newHeight);
            setWidth(newWidth);
        }
    }, [elementRef]);

    useEffect((): void => {
        if (elementRef.current !== null && !windowIsResizing) {
            getSize();
        }
    }, [elementRef, windowIsResizing, getSize]);

    return {
        getSize,
        height,
        width,
    };
};
